import { Component, inject, signal } from '@angular/core';

import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { UploadDownloadService } from '@shared/services/upload-download.service';

/**
 * Datos de entrada del diálogo de gestión de archivos.
 *  - value:       key S3 del archivo ya cargado (o '' si no hay).
 *  - label:       título visible.
 *  - accept:      filtro del input file (ej. 'application/pdf').
 *  - uploadPath:  path lógico en S3.
 *  - uploadFolio: sub-carpeta opcional.
 */
export interface FileManagerData {
  value?:       string;
  label?:       string;
  accept?:      string;
  uploadPath?:  string;
  uploadFolio?: string;
  /**
   * Permitir descargar el archivo desde el diálogo. Por omisión `true`.
   *
   * Se pone en `false` para documentos que sólo deben consultarse en la
   * plataforma y no salir de ella (p. ej. los PNOs de Procedimientos: son
   * documentos controlados, se leen aquí pero no se distribuyen).
   */
  allowDownload?: boolean;
  /**
   * Tamaño máximo (MB) permitido para archivos que NO son video. Por
   * omisión aplica el límite general del servidor (10 MB); módulos con
   * documentos pesados (p. ej. Procedimientos y Machotes: 50 MB) lo suben.
   */
  maxMb?: number;
}

/**
 * Diálogo reutilizable para cargar UN documento a S3, previsualizarlo
 * (PDF embebido o imagen) y descargarlo. Devuelve la key S3 al cerrar
 * con "Guardar" (o '' si se quitó el archivo); `null` si se cancela.
 */
@Component({
  selector: 'app-file-manager-dialog',
  standalone: true,
  imports: [
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule
],
  template: `
    <h2 mat-dialog-title>{{ data.label || 'Documento' }}</h2>

    <mat-dialog-content class="fm-content"
                        [class.fm-dragging]="dragging()"
                        (dragover)="onDragOver($event)"
                        (dragleave)="onDragLeave($event)"
                        (drop)="onDrop($event)">
      <input #fileInput type="file" hidden [accept]="data.accept || '*/*'"
             (change)="onSelected($event)" />

      @if (dragging()) {
        <div class="fm-drop-overlay">
          <mat-icon>cloud_upload</mat-icon>
          <p>Suelta el documento para cargarlo</p>
        </div>
      }

      <p class="fm-hint">
        <mat-icon>info</mat-icon>
        Arrastra y suelta un archivo aquí, o usa el botón.
      </p>

      <div class="fm-actions">
        <button mat-stroked-button color="primary" type="button"
                [disabled]="uploading()" (click)="fileInput.click()">
          <mat-icon>{{ value() ? 'autorenew' : 'upload_file' }}</mat-icon>
          {{ value() ? 'Reemplazar' : 'Cargar documento' }}
        </button>

        @if (value() && canDownload) {
          <button mat-stroked-button color="primary" type="button"
                  [disabled]="downloading()" (click)="download()">
            <mat-icon>download</mat-icon> Descargar
          </button>
        }
        @if (value()) {
          <button mat-icon-button color="warn" type="button"
                  matTooltip="Quitar" (click)="clear()">
            <mat-icon>delete</mat-icon>
          </button>
        }
      </div>

      @if (errorMsg()) {
        <div class="fm-error">
          <mat-icon>error_outline</mat-icon>
          <span>{{ errorMsg() }}</span>
        </div>
      }

      @if (uploading()) {
        <div class="fm-status">
          <mat-spinner diameter="22"></mat-spinner> <span>Subiendo...</span>
        </div>
      } @else if (!value()) {
        <div class="fm-empty">
          <mat-icon>description</mat-icon>
          <p>No hay documento cargado.</p>
        </div>
      } @else {
        <p class="fm-name">{{ fileName() }}</p>
        <div class="fm-preview">
          @if (previewLoading()) {
            <div class="fm-status"><mat-spinner diameter="22"></mat-spinner> <span>Cargando vista previa...</span></div>
          } @else if (isImage() && previewUrl()) {
            <img [src]="rawUrl()" alt="Vista previa" />
          } @else if (isPdf() && safeUrl()) {
            <iframe [src]="safeUrl()" title="Vista previa"></iframe>
          } @else {
            <div class="fm-empty">
              <mat-icon>visibility_off</mat-icon>
              @if (canDownload) {
                <p>Vista previa no disponible para este tipo de archivo. Usa "Descargar".</p>
              } @else {
                <p>Vista previa no disponible para este tipo de archivo.</p>
              }
            </div>
          }
        </div>
      }
    </mat-dialog-content>

    <mat-dialog-actions align="end">
      <button mat-button (click)="cancel()">Cancelar</button>
      <button mat-flat-button color="primary" (click)="save()" [disabled]="uploading()">
        Guardar
      </button>
    </mat-dialog-actions>
  `,
  styles: [`
    .fm-content { position: relative; min-width: 420px; max-width: 640px; display: flex; flex-direction: column; gap: 12px; }
    .fm-content.fm-dragging { outline: 2px dashed #1010eb; outline-offset: -6px; }
    .fm-drop-overlay {
      position: absolute; inset: 0; z-index: 5;
      display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 8px;
      background: rgba(16, 16, 235, .08); color: #1010eb; pointer-events: none; border-radius: 6px;
    }
    .fm-drop-overlay mat-icon { font-size: 44px; width: 44px; height: 44px; }
    .fm-hint { display: flex; align-items: center; gap: 6px; font-size: 12px; color: rgba(0,0,0,.5); margin: 0; }
    .fm-hint mat-icon { font-size: 16px; width: 16px; height: 16px; }
    .fm-actions { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
    .fm-status { display: flex; align-items: center; gap: 8px; color: rgba(0,0,0,.6); font-size: 14px; }
    .fm-error { display: flex; align-items: center; gap: 8px; font-size: 13px; color: #cc0025;
                background: #ffdde3; border: 1px solid #ffbac7; border-radius: 6px; padding: 8px 10px; }
    .fm-error mat-icon { font-size: 18px; width: 18px; height: 18px; }
    .fm-empty { display: flex; flex-direction: column; align-items: center; gap: 6px; padding: 24px; color: rgba(0,0,0,.45); text-align: center; }
    .fm-empty mat-icon { font-size: 40px; width: 40px; height: 40px; }
    .fm-name { font-size: 13px; color: rgba(0,0,0,.7); margin: 0; word-break: break-all; }
    .fm-preview { width: 100%; border: 1px solid #e0e0e0; border-radius: 6px; overflow: hidden; background: #fafafa; }
    .fm-preview img { width: 100%; max-height: 420px; object-fit: contain; display: block; }
    .fm-preview iframe { width: 100%; height: 460px; border: 0; }
  `],
})
export class FileManagerDialogComponent {
  private readonly uploads = inject(UploadDownloadService);
  private readonly sanitizer = inject(DomSanitizer);
  readonly dialogRef = inject(MatDialogRef<FileManagerDialogComponent>);
  readonly data = inject<FileManagerData>(MAT_DIALOG_DATA);

  /** ¿Se puede descargar? Por omisión sí; los PNOs lo desactivan. */
  readonly canDownload = this.data?.allowDownload !== false;

  value          = signal<string>(this.data?.value ?? '');
  uploading      = signal<boolean>(false);
  downloading    = signal<boolean>(false);
  previewLoading = signal<boolean>(false);
  errorMsg       = signal<string>('');           // mensaje de error visible en el diálogo
  rawUrl         = signal<string>('');           // presigned URL (img / descarga)
  safeUrl        = signal<SafeResourceUrl | null>(null); // sanitizada (iframe PDF)

  constructor() {
    if (this.value()) this.loadPreview();
  }

  fileName(): string {
    return (this.value() || '').split('/').pop() || 'documento';
  }

  private ext(): string {
    return (this.fileName().split('.').pop() || '').toLowerCase();
  }

  isImage(): boolean {
    return ['png', 'jpg', 'jpeg', 'webp', 'gif', 'bmp'].includes(this.ext());
  }

  isPdf(): boolean {
    return this.ext() === 'pdf';
  }

  previewUrl(): string {
    return this.rawUrl();
  }

  /** Genera una presigned URL fresca para previsualizar. */
  private async loadPreview(): Promise<void> {
    this.previewLoading.set(true);
    this.rawUrl.set('');
    this.safeUrl.set(null);
    this.errorMsg.set('');
    try {
      const file = await this.uploads.presign(this.value());
      if (file?.url) {
        this.rawUrl.set(file.url);
        this.safeUrl.set(this.sanitizer.bypassSecurityTrustResourceUrl(file.url));
      } else {
        this.errorMsg.set('No se pudo generar el enlace del documento. Verifica que el archivo siga existiendo.');
      }
    } catch (err: any) {
      console.error('[FileManager] Error generando vista previa:', err);
      this.errorMsg.set(`No se pudo abrir el documento: ${err?.message ?? err}`);
    } finally {
      this.previewLoading.set(false);
    }
  }

  async onSelected(event: Event): Promise<void> {
    const target = event.target as HTMLInputElement;
    const file = target.files?.[0];
    if (file) await this.uploadFile(file);
    target.value = '';
  }

  /** Sube el archivo a S3 y prepara su vista previa. */
  /**
   * Límites del servidor (upload_pdf.py). Se comprueban ANTES de subir para
   * no hacer esperar al usuario a que viaje un archivo que va a ser
   * rechazado al final.
   */
  private static readonly MAX_FILE_MB  = 10;
  private static readonly MAX_VIDEO_MB = 100;
  /** Extensiones que el servidor acepta. */
  private static readonly ALLOWED_EXT = [
    'pdf', 'jpg', 'jpeg', 'png', 'webp', 'gif',
    'mp4', 'mov', 'webm',
    'doc', 'docx', 'xls', 'xlsx',
  ];

  /**
   * Revisa tipo y tamaño antes de subir.
   *
   * Antes se subía cualquier cosa y el rechazo llegaba del servidor después
   * de mandar el archivo entero: con un logo en SVG o una imagen pesada, el
   * usuario esperaba y sólo entonces veía un error. Devuelve el motivo, o
   * `null` si el archivo es aceptable.
   */
  private rejectReason(file: File): string | null {
    const name = file?.name ?? '';
    const ext  = name.includes('.') ? name.split('.').pop()!.toLowerCase() : '';

    if (!FileManagerDialogComponent.ALLOWED_EXT.includes(ext)) {
      return `El formato .${ext || 'desconocido'} no está permitido. `
        + 'Puedes subir PDF, Word, Excel, JPG, PNG, WEBP, GIF, MP4, MOV o WEBM.'
        + (ext === 'svg' ? ' Para un logo en SVG, expórtalo antes a PNG.' : '');
    }

    const esVideo = ['mp4', 'mov', 'webm'].includes(ext);
    const maxMb   = esVideo
      ? FileManagerDialogComponent.MAX_VIDEO_MB
      : (this.data?.maxMb ?? FileManagerDialogComponent.MAX_FILE_MB);

    if (file.size > maxMb * 1024 * 1024) {
      const pesa = (file.size / (1024 * 1024)).toFixed(1);
      return `El archivo pesa ${pesa} MB y el máximo permitido es ${maxMb} MB.`;
    }

    return null;
  }

  private async uploadFile(file: File): Promise<void> {
    const motivo = this.rejectReason(file);
    if (motivo) {
      this.errorMsg.set(motivo);
      return;
    }

    this.errorMsg.set('');
    this.uploading.set(true);
    try {
      const uploaded = await this.uploads.upload({
        path:  this.data.uploadPath ?? 'uploads',
        folio: this.data.uploadFolio ?? '',
        file,
      });
      this.value.set(uploaded.filename);
      // Reutiliza la URL recién devuelta para la vista previa inmediata.
      this.rawUrl.set(uploaded.url);
      this.safeUrl.set(this.sanitizer.bypassSecurityTrustResourceUrl(uploaded.url));
    } catch (err: any) {
      console.error('[FileManager] Error subiendo documento:', err);
      this.errorMsg.set(`No se pudo subir el documento: ${err?.message ?? err}`);
    } finally {
      this.uploading.set(false);
    }
  }

  // ─── Arrastrar y soltar ───────────────────────────────────────────────────
  dragging = signal<boolean>(false);

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.dragging.set(true);
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.dragging.set(false);
  }

  async onDrop(event: DragEvent): Promise<void> {
    event.preventDefault();
    this.dragging.set(false);
    const file = event.dataTransfer?.files?.[0];
    if (file) await this.uploadFile(file);
  }

  /**
   * Descarga el documento.
   *
   * Estrategia (robusta ante URLs caducadas y CORS):
   *   1. Pedimos SIEMPRE una presigned URL fresca (la de la vista previa
   *      pudo haber caducado, lo que hacía que la descarga "no sirviera").
   *   2. Intentamos fetch → blob → <a download> para forzar una descarga
   *      real con el nombre del archivo.
   *   3. Si el fetch falla (p. ej. CORS del bucket S3 no permite XHR desde
   *      el origen de la app), caemos a un <a target="_blank"> con la URL
   *      firmada. Se usa un <a>.click() en lugar de window.open porque este
   *      último, al ejecutarse después de un `await`, suele quedar bloqueado
   *      por el bloqueador de pop-ups y por eso no pasaba nada.
   */
  async download(): Promise<void> {
    // Doble candado: si el consumidor deshabilitó la descarga, no la hacemos
    // aunque alguien llame al método directamente.
    if (!this.canDownload) return;

    this.downloading.set(true);
    this.errorMsg.set('');
    try {
      // 1) URL fresca (con respaldo a la que ya teníamos).
      let url = this.rawUrl();
      let presignError = '';
      try {
        const file = await this.uploads.presign(this.value());
        if (file?.url) url = file.url;
      } catch (e: any) {
        presignError = e?.message ?? String(e);
      }
      if (!url) {
        // Antes se hacía `window.open('')`, que abría una pestaña en blanco
        // (o nada) y el usuario creía que "la descarga no servía". Ahora se
        // le dice explícitamente qué pasó.
        this.errorMsg.set(
          `No se pudo obtener el enlace de descarga${presignError ? `: ${presignError}` : '.'}`
        );
        return;
      }

      // 2) Intento ideal: descarga real vía blob.
      try {
        const resp = await fetch(url);
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const blob = await resp.blob();
        const objectUrl = URL.createObjectURL(blob);
        this.triggerAnchor(objectUrl, this.fileName(), false);
        // Revocamos con un pequeño retraso para no cancelar la descarga.
        setTimeout(() => URL.revokeObjectURL(objectUrl), 4000);
      } catch {
        // 3) Respaldo: abrir la URL firmada en una nueva pestaña.
        this.triggerAnchor(url, this.fileName(), true);
      }
    } finally {
      this.downloading.set(false);
    }
  }

  /**
   * Crea un <a>, lo dispara y lo elimina. Con `newTab=false` fuerza descarga
   * (atributo download); con `newTab=true` abre en otra pestaña (respaldo para
   * URLs cross-origin donde el atributo download se ignora).
   */
  private triggerAnchor(href: string, name: string, newTab: boolean): void {
    const a = document.createElement('a');
    a.href = href;
    if (newTab) {
      a.target = '_blank';
      a.rel = 'noopener';
    } else {
      a.download = name;
    }
    document.body.appendChild(a);
    a.click();
    a.remove();
  }

  clear(): void {
    this.value.set('');
    this.rawUrl.set('');
    this.safeUrl.set(null);
  }

  save(): void {
    this.dialogRef.close(this.value());
  }

  cancel(): void {
    this.dialogRef.close(null);
  }
}
